/**
 * Static mock data for the Send flow gallery screens.
 *
 * Mirrors what the production send widget gets from the backend (pre-quote,
 * catalog countries, saved cards, operations list, receipts) but resolved
 * entirely client-side. Seeded into `useSendStore` / `useComposeStore` by
 * `useSeedMockData`.
 */
import { TransactionsStatus } from "../utils/commons";
import type { ITransaction } from "../interfaces/transactions";
import {
  PayoutMethodType,
  type EstimateOption,
  type EstimateResult,
  type RecipientInput,
} from "../interfaces/payout";
import type {
  RecipientBookEntry,
  RecipientDestination,
} from "../api/recipients";
import type { ICardPaymentMethod } from "../interfaces/payment-method";
import type { CountryCapability } from "../utils/catalogCountries";
import type { ISendDraft } from "../store/useSendStore";
import type { FundingMethod } from "../utils/sendCatalog";
import { buildMockQuote } from "./mockData";

export const SEND_MOCK_FX = 17.42;

export interface SendFundingMethodDetails {
  fee: number | null;
  minAmount: number | null;
  maxAmount: number | null;
}

/** Distinct per-method send bounds for the gallery funding picker (no network). */
export const sendFundingDetails: Partial<
  Record<FundingMethod, SendFundingMethodDetails>
> = {
  DEBIT_CARD: { fee: 1, minAmount: 10, maxAmount: 200 },
  CASH_DEPOSIT: { fee: 1.5, minAmount: 25, maxAmount: 500 },
  ACH: { fee: 0.5, minAmount: null, maxAmount: 2500 },
};

export const sendOriginCountries: CountryCapability[] = [
  {
    countryCode: "US",
    countryName: "United States",
    currencyCode: "USD",
    supportedByCorridors: ["US-MX", "US-CO", "US-AR"],
    effectiveStatus: "AVAILABLE",
  },
  {
    countryCode: "MX",
    countryName: "Mexico",
    currencyCode: "MXN",
    supportedByCorridors: ["MX-CO"],
    effectiveStatus: "AVAILABLE",
  },
];

export const sendCountries: CountryCapability[] = [
  {
    countryCode: "AR",
    countryName: "Argentina",
    currencyCode: "ARS",
    supportedByCorridors: ["US-AR"],
    effectiveStatus: "AVAILABLE",
  },
  {
    countryCode: "MX",
    countryName: "Mexico",
    currencyCode: "MXN",
    supportedByCorridors: ["US-MX"],
    effectiveStatus: "AVAILABLE",
  },
  {
    countryCode: "BR",
    countryName: "Brazil",
    currencyCode: "BRL",
    supportedByCorridors: ["US-BR"],
    effectiveStatus: "AVAILABLE",
  },
  {
    countryCode: "CO",
    countryName: "Colombia",
    currencyCode: "COP",
    supportedByCorridors: ["US-CO"],
    effectiveStatus: "AVAILABLE",
  },
  {
    countryCode: "GT",
    countryName: "Guatemala",
    currencyCode: "GTQ",
    supportedByCorridors: ["US-GT"],
    effectiveStatus: "AVAILABLE",
  },
];

const baseOptions = (
  originCountry: string,
  destinationCountry: string,
  destinationCurrency: string,
  sourceCurrency: string
): EstimateOption[] => {
  const corridorCode = `${originCountry}-${destinationCountry}`;
  const feeMultiplier = originCountry === "MX" ? 1.25 : 1;
  return [
  {
    capabilityKey: `cap-bank-bbva-${corridorCode}`,
    corridorCode,
    payerCode: "BBVA",
    payoutMethod: PayoutMethodType.BANK_ACCOUNT,
    fundingMethod: "DEBIT_CARD",
    sendAmount: 100,
    receiveAmount: 1742,
    customerFee: Number((1 * feeMultiplier).toFixed(2)),
    customerFxRate: SEND_MOCK_FX,
    sourceCurrency,
    destinationCurrency,
  },
  {
    capabilityKey: `cap-bank-bbva-cash-${corridorCode}`,
    corridorCode,
    payerCode: "BBVA",
    payoutMethod: PayoutMethodType.BANK_ACCOUNT,
    fundingMethod: "CASH_DEPOSIT",
    sendAmount: 100,
    receiveAmount: 1735,
    customerFee: Number((1.5 * feeMultiplier).toFixed(2)),
    customerFxRate: SEND_MOCK_FX,
    sourceCurrency,
    destinationCurrency,
  },
  {
    capabilityKey: `${originCountry}:${destinationCountry}:CRB:CASH_PICKUP:JAL:GDL:ELEKTRA`,
    corridorCode,
    payerCode: "ELEKTRA",
    payoutMethod: PayoutMethodType.CASH_PICKUP,
    fundingMethod: "DEBIT_CARD",
    sendAmount: 100,
    receiveAmount: 1730,
    customerFee: Number((1.5 * feeMultiplier).toFixed(2)),
    customerFxRate: SEND_MOCK_FX,
    sourceCurrency,
    destinationCurrency,
  },
  {
    capabilityKey: `${originCountry}:${destinationCountry}:CRB:CASH_PICKUP:CMX:ANY:OXXO`,
    corridorCode,
    payerCode: "OXXO",
    payoutMethod: PayoutMethodType.CASH_PICKUP,
    fundingMethod: "DEBIT_CARD",
    sendAmount: 100,
    receiveAmount: 1728,
    customerFee: Number((1.6 * feeMultiplier).toFixed(2)),
    customerFxRate: SEND_MOCK_FX,
    sourceCurrency,
    destinationCurrency,
  },
  {
    capabilityKey: `cap-card-${corridorCode}`,
    corridorCode,
    payerCode: null,
    payoutMethod: PayoutMethodType.PUSH_TO_CARD,
    fundingMethod: "DEBIT_CARD",
    sendAmount: 100,
    receiveAmount: 1738,
    customerFee: Number((1.2 * feeMultiplier).toFixed(2)),
    customerFxRate: SEND_MOCK_FX,
    sourceCurrency,
    destinationCurrency,
  },
  {
    capabilityKey: `cap-wallet-telcel-${corridorCode}`,
    corridorCode,
    payerCode: "TELCEL",
    payoutMethod: PayoutMethodType.MOBILE_WALLET,
    fundingMethod: "DEBIT_CARD",
    sendAmount: 100,
    receiveAmount: 1725,
    customerFee: Number((1.7 * feeMultiplier).toFixed(2)),
    customerFxRate: SEND_MOCK_FX,
    sourceCurrency,
    destinationCurrency,
  },
];
};

const routeKey = (origin: string, destination: string) => `${origin}-${destination}`;


/** Static pre-quote options keyed by origin-destination route. */
export const sendPreQuoteByRoute: Record<string, EstimateOption[]> = {
  [routeKey("US", "AR")]: baseOptions("US", "AR", "ARS", "USD"),
  [routeKey("US", "MX")]: baseOptions("US", "MX", "MXN", "USD"),
  [routeKey("US", "BR")]: baseOptions("US", "BR", "BRL", "USD"),
  [routeKey("US", "CO")]: baseOptions("US", "CO", "COP", "USD"),
  [routeKey("US", "GT")]: baseOptions("US", "GT", "GTQ", "USD"),
  [routeKey("MX", "CO")]: baseOptions("MX", "CO", "COP", "MXN"),
};

/** @deprecated Use sendPreQuoteByRoute */
export const sendPreQuoteByDestination: Record<string, EstimateOption[]> = {
  AR: sendPreQuoteByRoute[routeKey("US", "AR")],
  MX: sendPreQuoteByRoute[routeKey("US", "MX")],
  BR: sendPreQuoteByRoute[routeKey("US", "BR")],
  CO: sendPreQuoteByRoute[routeKey("US", "CO")],
  GT: sendPreQuoteByRoute[routeKey("US", "GT")],
};

export const sendPreQuoteOptions = sendPreQuoteByDestination.MX;

export const buildSendPreQuote = (
  originCountry: string,
  destinationCountry: string,
  amount: number,
  anchor: "send" | "receive" = "send"
): EstimateResult => {
  const key = routeKey(originCountry, destinationCountry);
  const template =
    sendPreQuoteByRoute[key] ??
    sendPreQuoteByRoute[routeKey("US", destinationCountry)] ??
    sendPreQuoteOptions;
  // Templates are authored at sendAmount=100; receive-anchored scales off best receive.
  const templateBestReceive = Math.max(
    ...template.map((o) => o.receiveAmount),
    1
  );
  const scale =
    anchor === "receive" ? amount / templateBestReceive : amount / 100;
  const options = template.map((o) => ({
    ...o,
    sendAmount: Number((o.sendAmount * scale).toFixed(2)),
    receiveAmount: Number((o.receiveAmount * scale).toFixed(2)),
  }));
  const best = [...options].sort(
    (a, b) => b.receiveAmount - a.receiveAmount
  )[0];
  return {
    options,
    summary: {
      count: options.length,
      best,
      rankingMetric: "receiveAmount",
      asOf: new Date().toISOString(),
      indicative: true,
    },
  };
};

export const sendSavedCards: ICardPaymentMethod[] = [
  {
    paymentMethodId: "pm_gallery_visa",
    brand: "VISA",
    last4: "4242",
    expiryMonth: "12",
    expiryYear: "27",
    cardholderName: "Jane Doe",
    enabled: true,
    isDefault: true,
    fundingMethod: "DEBIT_CARD",
  },
  {
    paymentMethodId: "pm_gallery_mc",
    brand: "MASTERCARD",
    last4: "5555",
    expiryMonth: "08",
    expiryYear: "26",
    cardholderName: "Jane Doe",
    enabled: true,
    isDefault: false,
    fundingMethod: "CREDIT_CARD",
  },
];

interface MockCatalog {
  banks: Array<{ id: string; name: string; accountPrefixes?: string[] }>;
  cashStates: Array<{ code: string; name: string }>;
  cashPayers: Array<{ id: string; name: string }>;
  carriers: Array<{ id: string; name: string }>;
}

const CATALOG_BY_COUNTRY: Record<string, MockCatalog> = {
  MX: {
    banks: [
      { id: "BBVA", name: "BBVA México", accountPrefixes: ["012"] },
      { id: "BANORTE", name: "Banorte", accountPrefixes: ["072"] },
      { id: "SANTANDER", name: "Santander", accountPrefixes: ["014"] },
    ],
    cashStates: [
      { code: "CMX", name: "Ciudad de México" },
      { code: "JAL", name: "Jalisco" },
      // NLE stays in the catalog so the picker can hide states with no
      // priced cash SKU (parity with flow-send `noStates`).
      { code: "NLE", name: "Nuevo León" },
    ],
    cashPayers: [
      { id: "ELEKTRA", name: "Elektra" },
      { id: "OXXO", name: "Oxxo" },
    ],
    carriers: [
      { id: "TELCEL", name: "Telcel" },
      { id: "ATT", name: "AT&T México" },
    ],
  },
};

const EMPTY_CATALOG: MockCatalog = {
  banks: [],
  cashStates: [],
  cashPayers: [],
  carriers: [],
};

export const mockCatalogFor = (country: string): MockCatalog =>
  CATALOG_BY_COUNTRY[country?.toUpperCase()] ?? EMPTY_CATALOG;

/** Sender perspective: list shows sendAmount + sourceCurrency. */
export const sendMockTransactions: ITransaction[] = [
  {
    operationId: "op_gallery_send_completed",
    status: TransactionsStatus.COMPLETED,
    amount: "400.00",
    currency: "USD",
    date: "10/06/26",
    recipientName: "Maria Lopez",
  },
  {
    operationId: "op_gallery_send_processing",
    status: TransactionsStatus.PROCESSING,
    amount: "143.50",
    currency: "USD",
    date: "05/06/26",
    recipientName: "Juan Perez",
  },
  {
    operationId: "op_gallery_send_pending",
    status: TransactionsStatus.PENDING,
    amount: "68.89",
    currency: "USD",
    date: "04/06/26",
    recipientName: "Ana Ruiz",
  },
  {
    operationId: "op_gallery_send_failed",
    status: TransactionsStatus.FAILED,
    amount: "50.00",
    currency: "USD",
    date: "03/06/26",
    recipientName: "Luis Gomez",
  },
  {
    operationId: "op_gallery_send_canceled",
    status: TransactionsStatus.CANCELED,
    amount: "35.00",
    currency: "USD",
    date: "02/06/26",
    recipientName: "Sofia Ortiz",
  },
  {
    operationId: "op_gallery_send_expired",
    status: TransactionsStatus.EXPIRED,
    amount: "20.00",
    currency: "USD",
    date: "01/06/26",
    recipientName: "Carlos Mendoza",
  },
  {
    operationId: "op_gallery_cash_pending",
    status: TransactionsStatus.PENDING,
    amount: "23.13",
    currency: "USD",
    date: "08/06/26",
    cashDepositPending: true,
    recipientName: "Tomas Echeverria",
    depositName: "Tomas Echeverria",
  },
];

export interface SendMockReceipt {
  operationId: string;
  sendAmount: number;
  receiveAmount: number;
  totalCost: number;
  fee: number;
  fundingFee: number;
  transactionFee: number;
  taxFee: number;
  totalFee?: number;
  exchangeRate: number;
  sourceCurrency: string;
  destinationCurrency: string;
  status: string;
  createdAt: string;
  recipient?: RecipientInput;
  cancellation?: {
    allowed: boolean;
    mode?: "void" | "request";
    expiresAt?: string;
  };
  references?: Array<{
    type: string;
    value: string;
    label?: Record<string, string>;
    presentation: string;
  }>;
}

/** Static recipient book for the send gallery (mirrors GET …/recipients). */
export const sendMockRecipients: RecipientBookEntry[] = [
  {
    recipientKey: "rk_maria_lopez",
    name: { firstName: "Maria", lastName: "Lopez" },
    phone: "+52 55 1234 5678",
    dateOfBirth: "1990-01-15",
    lastUsedAt: "2026-06-10T12:00:00.000Z",
    timesUsed: 3,
    destinations: [
      {
        payoutMethod: PayoutMethodType.BANK_ACCOUNT,
        paymentMethodId: "pm_dest_maria_bbva",
        display: {
          bankName: "BBVA",
          maskedAccount: "••••5678",
        },
        lastCapabilityKey: "cap-bank-bbva-US-MX",
        lastUsedAt: "2026-06-10T12:00:00.000Z",
        timesUsed: 2,
      } satisfies RecipientDestination,
      {
        payoutMethod: PayoutMethodType.PUSH_TO_CARD,
        paymentMethodId: "pm_dest_maria_visa",
        display: {
          cardBrand: "visa",
          cardLast4: "1111",
        },
        lastCapabilityKey: "cap-card-US-MX",
        lastUsedAt: "2026-06-08T12:00:00.000Z",
        timesUsed: 1,
      } satisfies RecipientDestination,
      {
        payoutMethod: PayoutMethodType.CASH_PICKUP,
        cash: { stateCode: "MX-JAL", payerCode: "ELEKTRA" },
        lastCapabilityKey: "US:MX:CRB:CASH_PICKUP:JAL:GDL:ELEKTRA",
        lastUsedAt: "2026-05-20T12:00:00.000Z",
        timesUsed: 1,
      } satisfies RecipientDestination,
    ],
  },
  {
    recipientKey: "rk_carlos_ruiz",
    name: { firstName: "Carlos", lastName: "Ruiz" },
    lastUsedAt: "2026-06-05T12:00:00.000Z",
    timesUsed: 1,
    destinations: [
      {
        payoutMethod: PayoutMethodType.BANK_ACCOUNT,
        paymentMethodId: "pm_dest_carlos_banorte",
        display: {
          bankName: "BANORTE",
          maskedAccount: "••••9012",
        },
        lastCapabilityKey: "cap-bank-bbva-US-MX",
        lastUsedAt: "2026-06-05T12:00:00.000Z",
        timesUsed: 1,
      } satisfies RecipientDestination,
    ],
  },
];

export const sendMockReceipts: Record<string, SendMockReceipt> = {
  op_gallery_send_completed: {
    operationId: "op_gallery_send_completed",
    sendAmount: 400,
    receiveAmount: 6968,
    totalCost: 401,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: SEND_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
    status: "completed",
    createdAt: "2026-06-10T12:00:00.000Z",
    recipient: {
      fullName: "Maria Lopez",
      canonicalName: { firstName: "Maria", lastName: "Lopez" },
    },
    references: [
      {
        type: "PAYOUT_CODE",
        value: "998877",
        presentation: "PROMINENT",
        label: { en: "Payout code", es: "Clave de cobro", fr: "Code de paiement" },
      },
    ],
  },
  op_gallery_send_processing: {
    operationId: "op_gallery_send_processing",
    sendAmount: 143.5,
    receiveAmount: 2500,
    totalCost: 144.5,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: SEND_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
    status: "processing",
    createdAt: "2026-06-05T12:00:00.000Z",
    cancellation: {
      allowed: true,
      mode: "request",
      expiresAt: "2099-01-01T00:00:00.000Z",
    },
    recipient: {
      fullName: "Carlos Ruiz",
      canonicalName: { firstName: "Carlos", lastName: "Ruiz" },
    },
  },
  op_gallery_send_failed: {
    operationId: "op_gallery_send_failed",
    sendAmount: 50,
    receiveAmount: 800,
    totalCost: 51,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: SEND_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
    status: "failed",
    createdAt: "2026-06-03T12:00:00.000Z",
  },
  op_gallery_send_canceled: {
    operationId: "op_gallery_send_canceled",
    sendAmount: 35,
    receiveAmount: 550,
    totalCost: 36,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: SEND_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
    status: "cancelled",
    createdAt: "2026-06-02T12:00:00.000Z",
  },
  op_gallery_send_expired: {
    operationId: "op_gallery_send_expired",
    sendAmount: 20,
    receiveAmount: 300,
    totalCost: 21,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: SEND_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
    status: "expired",
    createdAt: "2026-06-01T12:00:00.000Z",
  },
};

export const DEFAULT_SEND_RECEIPT_ID = "op_gallery_send_completed";

export const resolveSendReceiptId = (
  paramId: string | undefined,
  pathname: string
): string => {
  if (paramId && sendMockReceipts[paramId]) return paramId;
  const lastSegment = pathname.split("/").filter(Boolean).pop();
  if (lastSegment && sendMockReceipts[lastSegment]) return lastSegment;
  return DEFAULT_SEND_RECEIPT_ID;
};

export const getSendMockReceipt = (
  paramId: string | undefined,
  pathname: string
): SendMockReceipt =>
  sendMockReceipts[resolveSendReceiptId(paramId, pathname)] ??
  sendMockReceipts[DEFAULT_SEND_RECEIPT_ID];

/** Pre-armed compose seed for the Transfer gallery screen. */
export const sendComposeSeed = {
  originCountry: "US",
  destinationCountry: "MX",
  funding: "DEBIT_CARD" as FundingMethod,
  fundingInstrument: {
    paymentMethodId: "pm_gallery_visa",
    brand: "VISA",
    last4: "4242",
  },
  selection: {
    method: PayoutMethodType.BANK_ACCOUNT,
    option: sendPreQuoteOptions[0],
    payout: { bankId: "BBVA", accountNumber: "012345678901234568" },
  },
  recipient: {
    fullName: "Maria Lopez",
    canonicalName: { firstName: "Maria", lastName: "Lopez" },
    phone: "+52 55 1234 5678",
  } satisfies RecipientInput,
  bookDestinations: sendMockRecipients[0].destinations,
};

export const sendMockDraft: ISendDraft = {
  sourceCountry: "US",
  destinationCountry: "MX",
  sourceCurrency: "USD",
  destinationCurrency: "MXN",
  amount: 400,
  fundingMethod: "DEBIT_CARD",
  payoutMethod: PayoutMethodType.BANK_ACCOUNT,
  payout: sendComposeSeed.selection.payout,
  recipient: sendComposeSeed.recipient,
  capabilityKey: "cap-bank-bbva",
  fundingPaymentMethodId: "pm_gallery_visa",
};

export const sendMockQuote = buildMockQuote(400);
